import React, { useState } from "react";

// Dashboard mockup using TailwindCSS + Material UI (MUI)
// Usage: paste into a React app (create-react-app or Next.js page) with Tailwind and @mui/material installed.

import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import WorkIcon from "@mui/icons-material/Work";
import DashboardIcon from "@mui/icons-material/Dashboard";
import HandshakeIcon from "@mui/icons-material/Handshake";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Check, Edit, Notes } from "@mui/icons-material";
import { PageHeader, PageShell } from "../../components/UI/Page";

// Table frame inside a card: hairline border, scrolls sideways on phones.
const TABLE_WRAP = "app-divider mt-4 overflow-x-auto rounded-xl border";

// Account health → translucent status tint (works on every theme surface).
const HEALTH_TONES = {
  Healthy: "border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  "Needs Attention": "border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-300",
  "At Risk": "border-red-500/30 bg-red-500/15 text-red-700 dark:text-red-300",
  Inactive: "border-slate-500/25 bg-slate-500/10 text-slate-600 dark:text-slate-300",
};

const HealthChip = ({ health }) => (
  <span
    className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
      HEALTH_TONES[health] || HEALTH_TONES.Inactive
    }`}
  >
    {health}
  </span>
);

const LogEntry = ({ log }) => (
  <div className="app-surface rounded-xl border p-3">
    <div className="flex items-start justify-between gap-3">
      <p className="app-accent text-sm font-semibold">{log.type}</p>
      <p className="app-muted whitespace-nowrap text-xs tabular-nums">{log.date}</p>
    </div>
    {log.note && <p className="mt-1 text-sm">{log.note}</p>}
  </div>
);

const NoMatches = () => (
  <p className="app-muted py-6 text-center text-sm">
    Nothing matches your search.
  </p>
);

export default function CRMDashboard() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");

  // Activity Log State
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityNote, setActivityNote] = useState("");
  const [logType, setLogType] = useState("Email");
  const [customType, setCustomType] = useState("");
  const [logDialog, setLogDialog] = useState(false);

  const handleOpenLogDialog = () => setLogDialog(true);
  const handleCloseLogDialog = () => setLogDialog(false);

  const handleAddLog = () => {
    const finalType =
      logType === "Other" ? customType.trim() || "Other" : logType;

    if (activityNote.trim() === "" && finalType.trim() === "") return;

    const newLog = {
      id: Date.now(),
      type: finalType,
      note: activityNote.trim(),
      date: new Date().toLocaleString(),
    };

    setActivityLogs((prev) => [newLog, ...prev]);
    setActivityNote("");
    setCustomType("");
    setLogType("Email");
  };

  // Reconnect Plan
  const [playbookOpen, setPlaybookOpen] = useState(false);
  const handleOpenPlaybook = () => setPlaybookOpen(true);
  const handleClosePlaybook = () => setPlaybookOpen(false);

  // Reconnect Task
  const [reengageOpen, setReengageOpen] = useState(false);
  const [reengageTask, setReengageTask] = useState({
    title: "",
    date: "",
    note: "",
  });
  const handleOpenReengage = () => setReengageOpen(true);
  const handleCloseReengage = () => setReengageOpen(false);

  const handleSaveReengage = () => {
    if (!reengageTask.title) return;
    console.log("✅ Created Reconnect:", reengageTask);
    // Optionally push to state, backend, etc.
    setReengageTask({ title: "", date: "", note: "" });
    handleCloseReengage();
  };

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountDialog, setAccountDialog] = useState(false);

  const handleOpenAccount = (account) => {
    setSelectedAccount(account);
    setAccountDialog(true);
  };

  const handleCloseAccount = () => {
    setSelectedAccount(null);
    setAccountDialog(false);
  };

  let stages = [
    "Qualification",
    "Discovery",
    "Proposal",
    "Negotiation",
    "Won",
    "Lost",
  ];

  // New Opportunity dialog state
  const [newOppDialog, setNewOppDialog] = useState(false);
  const [newOpp, setNewOpp] = useState({
    job: "",
    account: "",
    value: "",
    stage: stages?.[0] || "", // fallback if stages undefined
  });

  const handleOpenNewOppDialog = (accountName, defaultStage) => {
    setNewOpp({
      job: "",
      account: accountName || "",
      value: "",
      stage: defaultStage || stages?.[0] || "",
    });
    setNewOppDialog(true);
  };

  const handleCloseNewOppDialog = () => setNewOppDialog(false);

  const handleSaveNewOpp = () => {
    if (!newOpp.job || !newOpp.account || !newOpp.value) return;

    const newOpportunity = {
      id: Date.now(),
      ...newOpp,
    };

    setOpps((prev) => [...prev, newOpportunity]); // assuming opps is your state
    handleCloseNewOppDialog();
    setNewOpp({ job: "", account: "", value: "", stage: stages?.[0] || "" });
  };

  // Mock data (replace with real API calls)
  const leads = [
    {
      id: 1,
      name: "John Smith",
      company: "Abc Inc",
      source: "Website",
      status: "New",
    },
    {
      id: 2,
      name: "Sara Jones",
      company: "BuildCo",
      source: "Referral",
      status: "Contacted",
    },
    {
      id: 3,
      name: "Tom Lee",
      company: "OldCo",
      source: "Past Account",
      status: "Re-engage",
    },
  ];

  const contacts = [
    {
      id: 1,
      name: "Jane Doe",
      account: "Big C Lumber",
      role: "Decision Maker",
      lastContact: "2025-08-12",
    },
    {
      id: 2,
      name: "Mike Miller",
      account: "TechX",
      role: "Engineer",
      lastContact: "2025-06-01",
    },
  ];

  const accounts = [
    {
      id: 1,
      name: "Big C Lumber",
      industry: "Construction",
      lastActivity: "2025-08-12",
      health: "At Risk",
      totalSpent: 153200,
      contacts: [
        {
          id: 101,
          name: "Blake Note",
          email: "blake@bigclumber.com",
          phone: "555-0123",
        },
        {
          id: 102,
          name: "Blake 2",
          email: "blake2@bigclumber.com",
          phone: "555-4567",
        },
      ],
      activityLogs: [
        {
          id: 1,
          type: "Email",
          note: "Sent proposal for new site build",
          date: "2025-08-10 09:22 AM",
        },
        {
          id: 2,
          type: "Phone Call",
          note: "Discussed project delays, waiting for client approval",
          date: "2025-08-12 02:45 PM",
        },
        {
          id: 3,
          type: "Voicemail",
          note: "Left follow-up voicemail regarding payment terms",
          date: "2025-09-05 11:00 AM",
        },
      ],
    },
    {
      id: 2,
      name: "Abc Inc",
      industry: "Manufacturing",
      lastActivity: "2024-12-01",
      health: "Inactive",
      totalSpent: 87250,
      contacts: [
        {
          id: 201,
          name: "John",
          email: "john@abcinc.com",
          phone: "555-8899",
        },
        {
          id: 202,
          name: "Lisa",
          email: "lisa@abcinc.com",
          phone: "555-3322",
        },
      ],
      activityLogs: [
        {
          id: 4,
          type: "Email",
          note: "Sent holiday promotion — no response",
          date: "2024-12-05 10:15 AM",
        },
        {
          id: 5,
          type: "Other",
          note: "Marked as inactive after 90 days of no engagement",
          date: "2025-03-01 08:30 AM",
        },
      ],
    },
    {
      id: 3,
      name: "Summit",
      industry: "Retail",
      lastActivity: "2025-09-25",
      health: "Healthy",
      totalSpent: 312500,
      contacts: [
        {
          id: 301,
          name: "Emily",
          email: "emily@summit.com",
          phone: "555-0199",
        },
        {
          id: 302,
          name: "Derek",
          email: "derek@summit.com",
          phone: "555-7755",
        },
      ],
      activityLogs: [
        {
          id: 6,
          type: "Email",
          note: "Sent invoice for Q3 product shipment",
          date: "2025-09-25 10:00 AM",
        },
        {
          id: 7,
          type: "Phone Call",
          note: "Confirmed delivery and upcoming restock order",
          date: "2025-09-26 01:30 PM",
        },
      ],
    },
    {
      id: 4,
      name: "Northwind",
      industry: "Transportation",
      lastActivity: "2025-07-18",
      health: "Needs Attention",
      totalSpent: 196400,
      contacts: [
        {
          id: 401,
          name: "Marcus Lee",
          email: "marcus@northwind.com",
          phone: "555-9001",
        },
      ],
      activityLogs: [
        {
          id: 8,
          type: "Voicemail",
          note: "Left message regarding contract renewal",
          date: "2025-07-18 04:10 PM",
        },
        {
          id: 9,
          type: "Other",
          note: "No response, flagged for follow-up",
          date: "2025-08-01 09:00 AM",
        },
      ],
    },
  ];

  const [opps, setOpps] = useState([
    {
      id: 1,
      job: "Abc",
      value: 12000,
      stage: "Discovery",
      account: "Abc Inc",
      contact: {
        name: "John Smith",
        email: "john@abc.com",
        phone: "555-123-4567",
      },
      notes: [
        { id: 1, text: "Reached out via email", date: "2025-09-30" },
        { id: 2, text: "Waiting for reply", date: "2025-10-01" },
      ],
    },
    {
      id: 2,
      job: "Big C Lumber Renewal",
      value: 48000,
      stage: "Proposal",
      account: "Big C Lumber",
      contact: {
        name: "Sarah Jones",
        email: "sjones@bigc.com",
        phone: "555-987-6543",
      },
      notes: [{ id: 1, text: "Sent proposal", date: "2025-09-29" }],
    },
    {
      id: 3,
      job: "OldCo Reactivation",
      value: 8000,
      stage: "Qualification",
      account: "OldCo",
      contact: {
        name: "Mike Brown",
        email: "mike@oldco.com",
        phone: "555-333-2222",
      },
      notes: [],
    },
  ]);

  const [activeOpp, setActiveOpp] = useState(null);
  const [newNote, setNewNote] = useState("");

  const toggleEditing = (id, isEditing) => {
    setOpps((prev) =>
      prev.map((o) => (o.id === id ? { ...o, isEditingValue: isEditing } : o))
    );
  };

  const handleValueChange = (id, val) => {
    setOpps((prev) =>
      prev.map((o) => (o.id === id ? { ...o, value: Number(val) } : o))
    );
  };

  const moveOpp = (id, newStage) => {
    setOpps((prev) =>
      prev.map((o) => (o.id === id ? { ...o, stage: newStage } : o))
    );
  };

  const openNotesDialog = (opp) => {
    setActiveOpp(opp);
    setNewNote("");
  };

  const closeNotesDialog = () => {
    setActiveOpp(null);
  };

  const addNote = () => {
    if (!newNote.trim()) return;
    const updated = opps.map((o) =>
      o.id === activeOpp.id
        ? {
            ...o,
            notes: [
              ...o.notes,
              {
                id: Date.now(),
                text: newNote,
                date: new Date().toISOString().split("T")[0],
              },
            ],
          }
        : o
    );
    setOpps(updated);
    setNewNote("");
  };

  // Filter helpers
  const filteredLeads = leads.filter((l) =>
    (l.name + l.company + l.source).toLowerCase().includes(search.toLowerCase())
  );
  const filteredContacts = contacts.filter((c) =>
    (c.name + c.account).toLowerCase().includes(search.toLowerCase())
  );
  const filteredAccounts = accounts.filter((a) =>
    (a.name + a.industry).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="app-canvas min-h-[calc(100vh-140px)] w-full">
      <PageShell size="2xl">
        {/* Topbar */}
        <PageHeader
          icon={HandshakeIcon}
          tone="emerald"
          eyebrow="Sales & Leads"
          title="Big C CRM"
          description={
            <span className="inline-flex flex-wrap items-center gap-2">
              <span className="app-chip border-amber-500/30 bg-amber-500/15 font-semibold text-amber-700 dark:text-amber-300">
                Testing
              </span>
              Preview with sample data — changes on this page aren't saved.
            </span>
          }
          actions={
            <>
              <IconButton
                onClick={() => setDrawerOpen(true)}
                aria-label="Open CRM navigation"
              >
                <DashboardIcon />
              </IconButton>
              <TextField
                size="small"
                placeholder="Search leads, contacts, accounts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                inputProps={{ "aria-label": "Search leads, contacts, accounts" }}
                className="w-full sm:w-72"
              />
              <Button variant="contained">New Lead</Button>
            </>
          }
        />

        {/* Drawer / Sidebar */}
        <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box className="w-64 p-4">
            <Typography variant="h6" className="mb-4">
              Navigation
            </Typography>
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setActiveTab(0);
                    setDrawerOpen(false);
                  }}
                >
                  <ListItemIcon>
                    <InboxIcon />
                  </ListItemIcon>
                  <ListItemText primary="Overview" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setActiveTab(1);
                    setDrawerOpen(false);
                  }}
                >
                  <ListItemIcon>
                    <InboxIcon />
                  </ListItemIcon>
                  <ListItemText primary="Leads" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setActiveTab(2);
                    setDrawerOpen(false);
                  }}
                >
                  <ListItemIcon>
                    <PeopleIcon />
                  </ListItemIcon>
                  <ListItemText primary="Contacts" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setActiveTab(3);
                    setDrawerOpen(false);
                  }}
                >
                  <ListItemIcon>
                    <BusinessIcon />
                  </ListItemIcon>
                  <ListItemText primary="Accounts" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setActiveTab(4);
                    setDrawerOpen(false);
                  }}
                >
                  <ListItemIcon>
                    <WorkIcon />
                  </ListItemIcon>
                  <ListItemText primary="Opportunities" />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </Drawer>

        {/* Main Content */}
        <div className="app-divider border-b">
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab label="Overview" />
            <Tab label="Leads" />
            <Tab label="Contacts" />
            <Tab label="Accounts" />
            <Tab label="Opportunities" />
          </Tabs>
        </div>

        <div className="mt-6">
          {activeTab === 0 && (
            <div className="space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="app-card flex flex-col rounded-2xl border p-5">
                  <p className="app-muted text-sm font-medium">Pipeline Value</p>
                  <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight">
                    $1
                  </p>
                  <p className="app-muted mt-1 text-xs">Weighted forecast: $1</p>
                </div>

                <div className="app-card flex flex-col rounded-2xl border p-5">
                  <p className="app-muted text-sm font-medium">Inactive Accounts</p>
                  <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight">
                    12
                  </p>
                  <p className="app-muted mt-1 text-xs">
                    Accounts Inactive &gt; 6 months
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleOpenPlaybook}
                    >
                      Run Reconnect Plan
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleOpenReengage}
                    >
                      Create Reconnect Task
                    </Button>
                  </div>
                </div>

                <div className="app-card flex flex-col rounded-2xl border p-5">
                  <p className="app-muted text-sm font-medium">New Leads (30d)</p>
                  <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight">
                    27
                  </p>
                  <p className="app-muted mt-1 text-xs">Top source: Website</p>
                </div>
              </div>

              {/* Quick lists: Inactive accounts + Suggested re-engages */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="app-card min-w-0 rounded-2xl border p-5 lg:col-span-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-lg font-semibold tracking-tight">
                      Suggested Reconnects
                    </h2>
                    <Button size="small" variant="outlined">
                      Run Auto-Suggest
                    </Button>
                  </div>

                  <TableContainer className={TABLE_WRAP}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Account</TableCell>
                          <TableCell>Last Activity</TableCell>
                          <TableCell>Suggested Action</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {accounts.map((a) => (
                          <TableRow key={a.id} hover>
                            <TableCell className="font-medium">{a.name}</TableCell>
                            <TableCell className="whitespace-nowrap tabular-nums">
                              {a.lastActivity}
                            </TableCell>
                            <TableCell>
                              <HealthChip health={a.health} />
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="outlined"
                                className="whitespace-nowrap"
                                onClick={() =>
                                  handleOpenNewOppDialog(a.name, stages[0])
                                } // auto-fill account & stage
                              >
                                Create Opportunity
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>

                <div className="app-card rounded-2xl border p-5">
                  <h2 className="text-lg font-semibold tracking-tight">
                    Recent Activity
                  </h2>
                  <ul className="app-divider mt-3 divide-y divide-[color:var(--app-divider)] text-sm">
                    <li className="py-2.5">Call logged with Jane Doe — 8/12/2025</li>
                    <li className="py-2.5">Email opened by Mike — 9/01/2025</li>
                    <li className="py-2.5">Proposal sent to Big C Lumber — 9/10/2025</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 1 && (
            <div className="app-card rounded-2xl border p-5">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-semibold tracking-tight">Leads</h2>
                <Button variant="contained">Add Lead</Button>
              </div>

              <TableContainer className={TABLE_WRAP}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLeads.map((l) => (
                      <TableRow key={l.id} hover>
                        <TableCell className="font-medium">{l.name}</TableCell>
                        <TableCell>{l.company}</TableCell>
                        <TableCell>{l.source}</TableCell>
                        <TableCell>
                          <Chip size="small" label={l.status} />
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small">Convert</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {filteredLeads.length === 0 && <NoMatches />}
            </div>
          )}

          {activeTab === 2 && (
            <div className="app-card rounded-2xl border p-5">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-semibold tracking-tight">Contacts</h2>
                <Button variant="contained">Add Contact</Button>
              </div>

              <TableContainer className={TABLE_WRAP}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Account</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Last Contact</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredContacts.map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell className="font-medium">{c.name}</TableCell>
                        <TableCell>{c.account}</TableCell>
                        <TableCell>{c.role}</TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {c.lastContact}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            className="whitespace-nowrap"
                            onClick={() => handleOpenLogDialog(c)}
                          >
                            Log Activity
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {filteredContacts.length === 0 && <NoMatches />}
            </div>
          )}

          {activeTab === 3 && (
            <div className="app-card rounded-2xl border p-5">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-semibold tracking-tight">Accounts</h2>
                <Button variant="contained">Add Account</Button>
              </div>

              <TableContainer className={TABLE_WRAP}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell>Industry</TableCell>
                      <TableCell>Last Activity</TableCell>
                      <TableCell>Health</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAccounts.map((a) => (
                      <TableRow key={a.id} hover>
                        <TableCell className="font-medium">{a.name}</TableCell>
                        <TableCell>{a.industry}</TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {a.lastActivity}
                        </TableCell>
                        <TableCell>
                          <HealthChip health={a.health} />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={() => handleOpenAccount(a)}
                          >
                            Open
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {filteredAccounts.length === 0 && <NoMatches />}
            </div>
          )}

          {activeTab === 4 && (
            <div>
              {/* Columns scroll sideways on narrow screens. */}
              <div className="-mx-4 flex gap-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
                {stages.map((stage, stageIndex, arr) => {
                  const stageOpps = opps.filter((o) => o.stage === stage);
                  return (
                    <div
                      key={stage}
                      className="app-surface flex min-w-[240px] flex-1 flex-col rounded-2xl border p-3"
                    >
                      <div className="mb-3 flex items-center justify-between gap-2 px-1">
                        <h3 className="text-sm font-semibold">{stage}</h3>
                        <span className="app-muted text-xs font-semibold tabular-nums">
                          {stageOpps.length}
                        </span>
                      </div>
                      <div className="space-y-3">
                        {stageOpps.map((o) => (
                          <div
                            key={o.id}
                            className="app-card flex flex-col items-start justify-between rounded-xl border p-3"
                          >
                            <div className="w-full">
                              <p className="text-sm font-semibold">{o.job}</p>

                              {/* Account and Value Row */}
                              <div className="mt-1 flex w-full items-center justify-between gap-2 text-sm">
                                <span className="app-muted min-w-0">
                                  {o.account} • $
                                  {o.isEditingValue ? (
                                    <TextField
                                      type="number"
                                      size="small"
                                      value={o.value}
                                      onChange={(e) =>
                                        handleValueChange(o.id, e.target.value)
                                      }
                                      onBlur={() => toggleEditing(o.id, false)}
                                      autoFocus
                                      inputProps={{ "aria-label": "Value" }}
                                      sx={{ width: 100 }}
                                    />
                                  ) : (
                                    <span className="font-semibold tabular-nums text-[color:var(--app-text)]">
                                      {o.value.toLocaleString()}
                                    </span>
                                  )}
                                </span>

                                <IconButton
                                  size="small"
                                  aria-label={
                                    o.isEditingValue ? "Done editing value" : "Edit value"
                                  }
                                  onClick={() =>
                                    toggleEditing(o.id, !o.isEditingValue)
                                  }
                                >
                                  {o.isEditingValue ? (
                                    <Check fontSize="small" />
                                  ) : (
                                    <Edit fontSize="small" />
                                  )}
                                </IconButton>
                              </div>

                              {/* Contact Info */}
                              <div className="app-muted mt-2 space-y-0.5 text-xs">
                                <div>
                                  <strong className="text-[color:var(--app-text)]">
                                    Contact:
                                  </strong>{" "}
                                  {o.contact.name}
                                </div>
                                <div className="break-all">Email: {o.contact.email}</div>
                                <div>Phone: {o.contact.phone}</div>
                              </div>
                            </div>

                            {/* Notes + stage move arrows */}
                            <div className="app-divider mt-3 flex w-full flex-wrap items-center gap-2 border-t pt-3">
                              <Button
                                variant="outlined"
                                size="small"
                                aria-label="Move to previous stage"
                                disabled={stageIndex === 0}
                                onClick={() =>
                                  moveOpp(o.id, stages[stageIndex - 1])
                                }
                                sx={{ minWidth: 40 }}
                              >
                                ←
                              </Button>
                              {stageIndex !== arr.length - 1 && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  aria-label="Move to next stage"
                                  onClick={() =>
                                    moveOpp(o.id, stages[stageIndex + 1])
                                  }
                                  sx={{ minWidth: 40 }}
                                >
                                  →
                                </Button>
                              )}
                              <Button
                                variant="outlined"
                                size="small"
                                className="!ml-auto"
                                startIcon={<Notes fontSize="small" />}
                                onClick={() => openNotesDialog(o)}
                              >
                                Notes
                              </Button>
                            </div>
                          </div>
                        ))}
                        {stageOpps.length === 0 && (
                          <p className="app-muted px-1 py-4 text-center text-xs">
                            No opportunities
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Notes Dialog */}
              <Dialog
                open={!!activeOpp}
                onClose={closeNotesDialog}
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle>
                  Notes for {activeOpp?.job} ({activeOpp?.account})
                </DialogTitle>
                <DialogContent dividers>
                  {activeOpp?.notes?.length > 0 ? (
                    <List>
                      {activeOpp.notes.map((note) => (
                        <ListItem key={note.id} disablePadding>
                          <ListItemText
                            primary={note.text}
                            secondary={note.date}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No notes yet.
                    </Typography>
                  )}

                  <TextField
                    label="Add new note"
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="!mt-4"
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeNotesDialog}>Close</Button>
                  <Button onClick={addNote} variant="contained">
                    Add Note
                  </Button>
                </DialogActions>
              </Dialog>
            </div>
          )}
        </div>
      </PageShell>

      <Dialog
        open={logDialog}
        onClose={handleCloseLogDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Log Activity</DialogTitle>
        <DialogContent className="space-y-4">
          {/* Log Type Selector */}
          <div className="flex flex-col gap-2 pt-1">
            <label
              htmlFor="crm-log-type"
              className="text-sm font-semibold"
            >
              Activity Type
            </label>
            <select
              id="crm-log-type"
              className="app-input rounded-xl border px-3 py-2 text-sm"
              value={logType}
              onChange={(e) => setLogType(e.target.value)}
            >
              <option value="Email">Email</option>
              <option value="Phone Call">Phone Call</option>
              <option value="Voicemail">Voicemail</option>
              <option value="Other">Other</option>
            </select>

            {logType === "Other" && (
              <TextField
                fullWidth
                label="Custom Type"
                value={customType}
                onChange={(e) => setCustomType(e.target.value)}
                className="!mt-2"
              />
            )}
          </div>

          {/* Log Note */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Activity Note"
            value={activityNote}
            onChange={(e) => setActivityNote(e.target.value)}
          />

          <div>
            <Button variant="contained" onClick={handleAddLog}>
              Add Log
            </Button>
          </div>

          {/* Activity History */}
          <div className="app-divider border-t pt-4">
            <h3 className="mb-2 font-semibold">Activity History</h3>
            <div className="max-h-60 space-y-2 overflow-y-auto">
              {activityLogs.length === 0 && (
                <p className="app-muted text-sm">No logs yet.</p>
              )}
              {activityLogs.map((log) => (
                <LogEntry key={log.id} log={log} />
              ))}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={playbookOpen}
        onClose={handleClosePlaybook}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reconnect Plan</DialogTitle>
        <DialogContent className="space-y-4">
          <p className="app-muted text-sm">Potential Steps?</p>
          <ul className="list-inside list-disc space-y-2 text-sm">
            <li>Send a email with a follow up</li>
            <li>Call contact to discuss</li>
            <li>Schedule time to follow up</li>
          </ul>
          <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
            Progress auto-saved to notes or automation log
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePlaybook}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reengageOpen}
        onClose={handleCloseReengage}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create Reconnect Task</DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-2">
            <TextField
              label="Title"
              fullWidth
              value={reengageTask.title}
              onChange={(e) =>
                setReengageTask({ ...reengageTask, title: e.target.value })
              }
            />
            <TextField
              label="Due Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={reengageTask.date}
              onChange={(e) =>
                setReengageTask({ ...reengageTask, date: e.target.value })
              }
            />
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={3}
              value={reengageTask.note}
              onChange={(e) =>
                setReengageTask({ ...reengageTask, note: e.target.value })
              }
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReengage}>Cancel</Button>
          <Button onClick={handleSaveReengage} variant="contained">
            Save Task
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={accountDialog}
        onClose={handleCloseAccount}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{selectedAccount?.name}</DialogTitle>

        <DialogContent className="space-y-6">
          {/* Summary Section */}
          <dl className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <div>
              <dt className="app-muted text-xs font-medium">Industry</dt>
              <dd className="mt-0.5 text-sm font-semibold">
                {selectedAccount?.industry}
              </dd>
            </div>
            <div>
              <dt className="app-muted text-xs font-medium">Health</dt>
              <dd className="mt-0.5 text-sm font-semibold">
                {selectedAccount?.health}
              </dd>
            </div>
            <div>
              <dt className="app-muted text-xs font-medium">Last Activity</dt>
              <dd className="mt-0.5 text-sm font-semibold tabular-nums">
                {selectedAccount?.lastActivity}
              </dd>
            </div>
            <div>
              <dt className="app-muted text-xs font-medium">Total Spent</dt>
              <dd className="mt-0.5 text-sm font-semibold tabular-nums">
                {selectedAccount?.totalSpent
                  ? `$${selectedAccount.totalSpent.toLocaleString()}`
                  : "$0"}
              </dd>
            </div>
            <div>
              <dt className="app-muted text-xs font-medium">Account ID</dt>
              <dd className="mt-0.5 text-sm font-semibold tabular-nums">
                {selectedAccount?.id}
              </dd>
            </div>
          </dl>

          {/* Contacts Section */}
          <div>
            <h3 className="mb-2 text-lg font-semibold">Contacts</h3>
            {selectedAccount?.contacts?.length > 0 ? (
              <div className="space-y-2">
                {selectedAccount.contacts.map((c) => (
                  <div
                    key={c.id}
                    className="app-surface flex items-center justify-between gap-3 rounded-xl border p-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{c.name}</p>
                      <p className="app-muted break-all text-sm">{c.email}</p>
                      <p className="app-muted text-sm tabular-nums">{c.phone}</p>
                    </div>
                    <Button size="small" variant="outlined">
                      View
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="app-muted text-sm">No contacts found.</p>
            )}
          </div>

          {/* Activity Logs */}
          <div>
            <h3 className="mb-2 text-lg font-semibold">Activity Logs</h3>
            {selectedAccount?.activityLogs?.length > 0 ? (
              <div className="max-h-60 space-y-2 overflow-y-auto">
                {selectedAccount.activityLogs.map((log) => (
                  <LogEntry key={log.id} log={log} />
                ))}
              </div>
            ) : (
              <p className="app-muted text-sm">No logs available.</p>
            )}
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseAccount}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={newOppDialog}
        onClose={handleCloseNewOppDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>New Opportunity</DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-2">
            <TextField
              label="Job"
              fullWidth
              value={newOpp.job}
              onChange={(e) => setNewOpp({ ...newOpp, job: e.target.value })}
            />
            <TextField
              label="Account"
              fullWidth
              value={newOpp.account}
              disabled // auto-filled, cannot edit
            />
            <TextField
              label="Value"
              type="number"
              fullWidth
              value={newOpp.value}
              onChange={(e) => setNewOpp({ ...newOpp, value: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Stage</InputLabel>
              <Select
                value={newOpp.stage}
                label="Stage"
                onChange={(e) => setNewOpp({ ...newOpp, stage: e.target.value })}
              >
                {stages.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewOppDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNewOpp}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
